import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import Header from '../../layout/Header'
import MobileNav from '../../layout/MobileNav'
import Checkbox from '../../components/fields/Checkbox';
import ReorderableSpotsList from '../../components/ReorderableSpotsList';
import { fetchAllSpots } from '../../store/slices/savedSpotsSlice';
import { getOrderedSpots } from '../../utils/helpers';
import {Helmet} from "react-helmet";

const DAILY_UNITS = [
    { key: 'wave_height_max', label: 'Wave Height' },
    { key: 'wave_period_max', label: 'Wave Period' },
    { key: 'wave_direction_dominant', label: 'Wave Direction' },
    { key: 'wind_speed_10m_max', label: 'Wind Speed' },
    { key: 'wind_direction_10m_dominant', label: 'Wind Direction' },
    { key: 'overall_rating_score', label: 'Rating' },
];

const HOURLY_UNITS = [
    { key: 'overall_rating_score', label: 'Rating' },
    { key: 'wave_height', label: 'Wave Height' },
    { key: 'swell_wave_height', label: 'Swell Wave Height' },
    { key: 'swell_wave_period', label: 'Swell Wave Period' },
    { key: 'swell_wave_direction', label: 'Swell Wave Direction' },
    { key: 'wind_direction_10m', label: 'Wind Direction' },
    { key: 'wind_speed_10m', label: 'Wind Speed 10m' },
    { key: 'wind_speed_80m', label: 'Wind Speed 80m' },
    { key: 'wind_gusts_10m', label: 'Wind Gusts' },
    { key: 'temperature_2m', label: 'Temperature' },
    { key: 'rain', label: 'Rain' },
    { key: 'cloud_cover', label: 'Cloud Cover' },
    { key: 'sea_level_height_msl', label: 'Sea Level Height (tide)' },
];

const ALL_HOURS = ['00:00', '01:00', '02:00', '03:00', '04:00', '05:00', '06:00', '07:00', '08:00', '09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00', '18:00', '19:00', '20:00', '21:00', '22:00', '23:00'];

const Options = () => {
    
    const dispatch = useDispatch();

    const savedSpots = useSelector((state) => state.savedSpots.spots);
    const spotOrder = useSelector((state) => state.savedSpots.spotOrder);
    const orderedSpots = getOrderedSpots(savedSpots, spotOrder);

    React.useEffect(() => {
        dispatch(fetchAllSpots());
    }, [dispatch]);

    const visibleUnitsDaily = useSelector((state) => state.dailyForecast.visibleUnitsDaily) || {
        wave_height_max: true,
        wave_direction_dominant: false,
        wave_period_max: false,
        wind_speed_10m_max: false,
        wind_direction_10m_dominant: false,
        overall_rating_score: true
    };

    const visibleUnitsHourly = useSelector((state) => state.hourlyForecast.visibleUnitsHourly) || {
        overall_rating_score: true,
        wave_height: true,
        swell_wave_height: true,
        swell_wave_period: true,
        swell_wave_direction: true,
        wind_speed_10m: true,
        wind_speed_80m: false,
        wind_gusts_10m: false,
        wind_direction_10m: true,
        temperature_2m: false,
        rain: false,
        cloud_cover: false,
        sea_level_height_msl: false,
    };

    const includedHours = useSelector((state) => state.hourlyForecast.includedHours) || [
        '05:00', '07:00', '09:00', '12:00', '14:00', '17:00', '19:00'
    ];

    const handleIncludedHoursChange = (hour) => {
        if (!includedHours) return;

        const newIncludedHours = includedHours.includes(hour)
            ? includedHours.filter(h => h !== hour)
            : [...includedHours, hour];

        dispatch({ type: 'hourlyForecast/setIncludedHours', payload: newIncludedHours });
    };

    const handleVisibleUnitsDailyChange = (unit) => {
        if (!visibleUnitsDaily) return;
        
        const newVisibleUnits = {
            ...visibleUnitsDaily,
            [unit]: !visibleUnitsDaily[unit],
        };
        dispatch({ type: 'dailyForecast/setVisibleUnitsDaily', payload: newVisibleUnits });
    }

    const handleVisibleUnitsHourlyChange = (unit) => {
        if (!visibleUnitsHourly) return;
        
        const newVisibleUnits = {
            ...visibleUnitsHourly,
            [unit]: !visibleUnitsHourly[unit],
        };
        dispatch({ type: 'hourlyForecast/setVisibleUnitsHourly', payload: newVisibleUnits });
    }

    return (
        <>
        <Helmet>
            <title>Settings | Oreca Pro</title>
            <link rel="canonical" href={window.location.href} />
        </Helmet>
        <Header />
        <MobileNav />
        <div className='pt-[76px] pb-[90px] px-4 max-w-[1100px] mx-auto'>
            <h1 className='text-2xl font-semibold mb-1 mt-2'>Settings</h1>
            
            {/* Cookie Settings Link */}
            <div className='max-w-[700px] mx-auto mt-5 mb-5'>
                <div className='bg-custom-charcoal/50 p-4 rounded-lg border border-gray-700'>
                    <div className='flex items-center justify-between'>
                        <div>
                            <h3 className='text-lg font-semibold text-white mb-1'>Cookie Preferences</h3>
                            <p className='text-sm text-gray-400'>Manage your cookie and analytics settings</p>
                        </div>
                        <Link 
                            to="/cookie-policy" 
                            className='bg-custom-light-blue text-white px-4 py-2 rounded hover:bg-blue-600 transition-colors text-sm'
                        >
                            Manage Cookies
                        </Link>
                    </div>
                </div>
            </div>

            {/* Reorder Saved Spots */}
            <div className='max-w-[700px] mx-auto mt-5 mb-5'>
                <p className="text-lg mb-1">Reorder Saved Spots</p>
                <p className='text-sm text-custom-light-grey mb-3'>Drag the handle to change the order your spots appear in.</p>
                {orderedSpots.length === 0 ? (
                    <p className='text-sm text-custom-light-grey'>Save a spot first to reorder it here.</p>
                ) : (
                    <ReorderableSpotsList spots={orderedSpots} />
                )}
            </div>
            
            <div className='max-w-[700px] mx-auto mt-10'>
                <div className='mt-6 mb-12'>
                    <p className="text-lg mb-3">Daily Forecast Units</p>
                    <div className='rounded-xl bg-custom-charcoal/30 divide-y divide-custom-charcoal overflow-hidden'>
                        {DAILY_UNITS.map(({ key, label }) => (
                            <Checkbox
                                key={key}
                                name={`daily-${key}`}
                                label={label}
                                checked={!!visibleUnitsDaily?.[key]}
                                onChange={() => handleVisibleUnitsDailyChange(key)}
                                className='px-4 py-3'
                            />
                        ))}
                    </div>
                </div>


                <div className='mt-6 mb-12'>
                    <p className="text-lg mb-3">Hourly Forecast Units</p>
                    <div className='rounded-xl bg-custom-charcoal/30 divide-y divide-custom-charcoal overflow-hidden'>
                        {HOURLY_UNITS.map(({ key, label }) => (
                            <Checkbox
                                key={key}
                                name={`hourly-${key}`}
                                label={label}
                                checked={!!visibleUnitsHourly?.[key]}
                                onChange={() => handleVisibleUnitsHourlyChange(key)}
                                className='px-4 py-3'
                            />
                        ))}
                    </div>
                </div>
                <div className='mt-6 mb-12'>
                    <p className="text-lg mb-3">Included Hours in Hourly Forecast</p>
                    <div className='rounded-xl bg-custom-charcoal/30 divide-y divide-custom-charcoal overflow-hidden'>
                        {ALL_HOURS.map((hour) => (
                            <Checkbox
                                key={hour}
                                name={`hour-${hour}`}
                                label={hour}
                                checked={!!includedHours?.includes(hour)}
                                onChange={() => handleIncludedHoursChange(hour)}
                                className='px-4 py-3'
                            />
                        ))}
                    </div>
                </div>
            </div>
        </div>
        </>
    );
};

export default Options;
